import React, { useState, useEffect } from 'react';
import { Camera, MapPin, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { getImageUrl } from '../config/runtime';

/**
 * Generic popup component for message pins on the map
 * Adapts content based on pin type (photo, audio, discovery, etc.)
 * 
 * Design: Matches UnifiedMap popup styling with cohesive visual language
 */
const MessagePinPopup = ({ pin, cluster = null, onPinNavigate = null, onViewDetails = null }) => {
  const [currentPinIndex, setCurrentPinIndex] = useState(0);
  
  // Handle cluster vs single pin
  const isCluster = cluster && cluster.pins.length > 1;
  const currentPin = isCluster ? cluster.pins[currentPinIndex] : pin;
  const totalPins = isCluster ? cluster.pins.length : 1;

  // Reset pin index when cluster changes
  useEffect(() => {
    setCurrentPinIndex(0);
  }, [cluster?.id]);

  // Validate currentPinIndex bounds
  useEffect(() => {
    if (currentPinIndex >= totalPins) {
      setCurrentPinIndex(0);
    }
  }, [currentPinIndex, totalPins]);

  if (!currentPin) return null;

  const formatTimestamp = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown time';
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid time';
      
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.warn('Error formatting timestamp:', error);
      return 'Unknown time';
    }
  };

  const formatCoordinates = (location) => {
    try {
      if (!location || typeof location.lat !== 'number' || typeof location.lng !== 'number') {
        return 'Unknown location';
      }
      return `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`;
    } catch (error) {
      console.warn('Error formatting coordinates:', error);
      return 'Unknown location';
    }
  };

  const navigatePin = (direction) => {
    if (!isCluster) return;
    
    const newIndex = direction === 'next' 
      ? (currentPinIndex + 1) % totalPins
      : (currentPinIndex - 1 + totalPins) % totalPins;
    
    setCurrentPinIndex(newIndex);
    
    if (onPinNavigate) {
      onPinNavigate(cluster.pins[newIndex]);
    }
  };

  const renderPinContent = () => {
    // Check if this is a detection pin (has detection metadata)
    if (currentPin.detectionLabel) {
      return <DetectionPinContent pin={currentPin} cluster={cluster} onViewDetails={onViewDetails} />;
    }
    
    switch (currentPin.type) {
      case 'PHOTO':
        return <PhotoPinContent pin={currentPin} formatTimestamp={formatTimestamp} />;
      
      case 'AUDIO':
        return <AudioPinContent pin={currentPin} />;
      
      case 'DISCOVERY':
        return <DiscoveryPinContent pin={currentPin} />;
      
      case 'MILESTONE':
        return <MilestonePinContent pin={currentPin} />;
      
      default:
        return <GenericPinContent pin={currentPin} />;
    }
  };

  return (
    <div className="message-pin-popup min-w-[240px] max-w-[300px] -m-3 overflow-hidden rounded-xl bg-white dark:bg-gray-800">
      {/* Header with navigation for clusters */}
      {isCluster && (
        <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
          <button
            onClick={() => navigatePin('prev')}
            className="w-7 h-7 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={totalPins <= 1}
          >
            <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
          
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {currentPinIndex + 1}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">of</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {totalPins}
            </span>
          </div>
          
          <button
            onClick={() => navigatePin('next')}
            className="w-7 h-7 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={totalPins <= 1}
          >
            <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      )}

      {/* Pin content based on type */}
      <div className="p-3">
        {renderPinContent()}
        
        {/* Footer with metadata - only for non-detection pins */}
        {!currentPin.detectionLabel && (
          <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
              <Clock className="w-3 h-3" />
              {formatTimestamp(currentPin.timestamp)}
            </div>
            
            <div className="text-gray-400 dark:text-gray-500 font-mono text-[10px]">
              {formatCoordinates(currentPin.location)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Photo pin content component
 */
const PhotoPinContent = ({ pin, formatTimestamp }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  
  const handleImageLoad = () => {
    setImageLoading(false);
  };
  
  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };
  
  return (
    <div className="photo-pin-content">
      {/* Photo preview with rounded corners */}
      <div className="mb-3 relative -mx-3 -mt-3">
        {!imageError && pin.imageUrl ? (
          <div className="relative">
            {imageLoading && (
              <div className="absolute inset-0 w-full h-36 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              </div>
            )}
            <img
              src={getImageUrl(pin.imageUrl)}
              alt="Discovery"
              className={`w-full h-36 object-cover transition-opacity ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            {/* Type badge */}
            <div className="absolute top-2.5 right-2.5 px-2 py-1 bg-blue-500/90 backdrop-blur-sm text-white text-[10px] font-medium rounded-md flex items-center gap-1">
              <Camera className="w-3 h-3" />
              Photo
            </div>
          </div>
        ) : (
          <div className="w-full h-28 bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-1.5 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Camera className="w-6 h-6 text-blue-500 dark:text-blue-400" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {imageError ? 'Failed to load' : 'No image'}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Message content */}
      {pin.content && pin.content.trim() && (
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed line-clamp-3">
          {pin.content}
        </p>
      )}
    </div>
  );
};

/**
 * Audio pin content component
 */
const AudioPinContent = ({ pin }) => {
  return (
    <div className="audio-pin-content">
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl">
        <div className="w-11 h-11 bg-emerald-100 dark:bg-emerald-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-xl">🎵</span>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Audio Recording</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Tap to play</p>
        </div>
      </div>
      
      {pin.content && (
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{pin.content}</p>
      )}
    </div>
  );
};

/**
 * Discovery pin content component
 */
const DiscoveryPinContent = ({ pin }) => {
  return (
    <div className="discovery-pin-content">
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-xl">
        <div className="w-11 h-11 bg-amber-100 dark:bg-amber-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-xl">⭐</span>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Discovery</p>
          {pin.discovery?.type && (
            <p className="text-xs text-amber-600 dark:text-amber-400 truncate">{pin.discovery.type}</p>
          )}
        </div>
      </div>
      
      {pin.content && (
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{pin.content}</p>
      )}
    </div>
  );
};

/**
 * Milestone pin content component
 */
const MilestonePinContent = ({ pin }) => {
  return (
    <div className="milestone-pin-content">
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 rounded-xl">
        <div className="w-11 h-11 bg-rose-100 dark:bg-rose-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-xl">🚩</span>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Milestone</p>
          {pin.milestone?.name && (
            <p className="text-xs text-rose-600 dark:text-rose-400 truncate">{pin.milestone.name}</p>
          )}
        </div>
      </div>
      
      {pin.content && (
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{pin.content}</p>
      )}
    </div>
  );
};

/**
 * Detection pin content component - minimal version for ThingsExplorer
 */
const DetectionPinContent = ({ pin, cluster, onViewDetails }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  
  const handleImageLoad = () => {
    setImageLoading(false);
  };
  
  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  
  return (
    <div className="detection-pin-content">
      {/* Detection label header */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
          <span className="text-base">🔍</span>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {pin.detectionLabel}
          </div>
          {pin.confidence != null && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="h-1 w-10 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                <div 
                  className={`h-full rounded-full ${
                    pin.confidence > 0.8 ? 'bg-emerald-500' : 
                    pin.confidence > 0.6 ? 'bg-blue-500' : 'bg-amber-500'
                  }`}
                  style={{ width: `${pin.confidence * 100}%` }}
                />
              </div>
              <span className="text-[10px] text-gray-400">{Math.round(pin.confidence * 100)}%</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Thumbnail with bbox overlay */}
      <div className="relative rounded-lg overflow-hidden">
        {!imageError && pin.imageUrl ? (
          <>
            {imageLoading && (
              <div className="absolute inset-0 w-full h-24 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent"></div>
              </div>
            )}
            <div className="relative">
              <img
                src={getImageUrl(pin.imageUrl)}
                alt="Detection"
                className={`w-full h-24 object-cover transition-opacity ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              {/* Bbox overlay */}
              {pin.bbox && (
                <div 
                  className="absolute border-2 border-emerald-400 rounded-sm pointer-events-none shadow-sm"
                  style={{
                    left: `${(pin.bbox.bbox?.x1 || 0) / 1200 * 100}%`,
                    top: `${(pin.bbox.bbox?.y1 || 0) / 800 * 100}%`,
                    width: `${((pin.bbox.bbox?.x2 || 0) - (pin.bbox.bbox?.x1 || 0)) / 1200 * 100}%`,
                    height: `${((pin.bbox.bbox?.y2 || 0) - (pin.bbox.bbox?.y1 || 0)) / 800 * 100}%`,
                  }}
                />
              )}
            </div>
          </>
        ) : (
          <div className="w-full h-20 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Camera className="w-5 h-5 text-gray-400 dark:text-gray-500 mx-auto" />
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                {imageError ? 'Failed to load' : 'No image'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Generic pin content component for unknown types
 */
const GenericPinContent = ({ pin }) => {
  return (
    <div className="generic-pin-content">
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 rounded-xl">
        <div className="w-11 h-11 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-xl">📍</span>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Location Pin</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Message marker</p>
        </div>
      </div>
      
      {pin.content && (
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{pin.content}</p>
      )}
    </div>
  );
};

export default MessagePinPopup;
